/**
 * src/pages/admin/AdminSupportChat.js
 *
 * Admin support center real-time chat dashboard.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { MessageSquare, Send, Shield, UserRound, PlusCircle } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import { useToast } from "../../contexts/ToastContext";
import { useAdmin } from "../../hooks/useAdmin";
import { userRepository } from "../../repositories/userRepository";
import {
  createOrGetSupportChat,
  listenMessages,
  listenSupportChatsForAdmin,
  sendSupportMessage,
} from "../../services/chatService";
import { logAdminAction } from "../../services/adminService";
import { errorDev } from '../../utils/logger.js';

function formatTime(ts) {
  if (!ts) return "";
  try {
    const date = typeof ts?.toDate === "function"
      ? ts.toDate()
      : ts?.seconds
        ? new Date(ts.seconds * 1000)
        : new Date(ts);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch (err) {
    errorDev("Operation failed:", err);
    return "";
  }
}

function formatListTime(ts) {
  if (!ts) return "";
  try {
    const date = typeof ts?.toDate === "function"
      ? ts.toDate()
      : ts?.seconds
        ? new Date(ts.seconds * 1000)
        : new Date(ts);
    return date.toLocaleDateString([], { month: "short", day: "numeric" });
  } catch (err) {
    errorDev("Operation failed:", err);
    return "";
  }
}

function supportLabel(chat) {
  return chat?.supportLabel || "Support Center";
}

function displayUserLabel(profile) {
  const displayName = String(profile?.displayName || profile?.name || profile?.fullName || "").trim();
  if (displayName) return displayName;

  const email = String(profile?.email || "").trim();
  if (email.includes("@")) return email.split("@")[0];

  const username = String(profile?.username || profile?.handle || "").trim();
  if (username) return username;

  return "Community member";
}

export default function AdminSupportChat() {
  const { user } = useAuth();
  const { isAdmin } = useAdmin();
  const toast = useToast();

  const [chats, setChats] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [composer, setComposer] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [targetEmail, setTargetEmail] = useState("");
  const [openingMessage, setOpeningMessage] = useState("Hello, this is Support Center. How can we help today?");
  const [profilesById, setProfilesById] = useState({});
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!isAdmin) return () => {};
    const unsub = listenSupportChatsForAdmin((rows) => {
      setChats(rows || []);
      setSelectedChatId((prev) => prev || rows?.[0]?.id || null);
    });
    return () => { unsub && unsub(); };
  }, [isAdmin]);

  useEffect(() => {
    if (!selectedChatId) {
      setMessages([]);
      return () => {};
    }
    const unsub = listenMessages(selectedChatId, (rows) => setMessages(rows || []));
    return () => { unsub && unsub(); };
  }, [selectedChatId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    let active = true;
    const supportUserIds = Array.from(new Set(
      (chats || [])
        .map((chat) => String(chat.supportUserId || ""))
        .filter(Boolean)
    ));

    (async () => {
      const missing = supportUserIds.filter((uid) => !profilesById[uid]);
      if (missing.length === 0) return () => { active = false; };

      let resolvedProfiles = {};
      if (missing.length === 1) {
        // Single id: use getById to skip the in-query overhead
        const profile = await userRepository.getById(missing[0]);
        if (active && profile) resolvedProfiles = { [profile.id]: { ...profile } };
      } else {
        const profiles = await userRepository.getByIds(missing);
        resolvedProfiles = Object.fromEntries(
          profiles.map((p) => [p.id, { ...p }])
        );
      }

      const updates = {};
      for (const uid of missing) {
        updates[uid] = resolvedProfiles[uid] || { id: uid };
      }
      if (active) {
        setProfilesById((prev) => ({ ...prev, ...updates }));
      }
    })();

    return () => { active = false; };
  }, [chats, profilesById]);

  const selectedChat = useMemo(
    () => chats.find((chat) => String(chat.id) === String(selectedChatId)) || null,
    [chats, selectedChatId]
  );

  const selectedUserProfile = selectedChat?.supportUserId
    ? profilesById[String(selectedChat.supportUserId)] || null
    : null;

  const findUserByEmail = async (email) => {
    // Route through userRepository so the email lookup shares one
    // query definition (and its raw-then-lowercase fallback) with
    // the rest of the app instead of building the query inline.
    return userRepository.findByEmail(email);
  };

  const handleCreateChat = async (e) => {
    e.preventDefault();
    const email = String(targetEmail || "").trim();
    if (!email || !user?.uid) return;

    setIsCreating(true);
    try {
      const matchedUser = await findUserByEmail(email);
      if (!matchedUser?.id) {
        toast.error("No user found for this email");
        return;
      }

      const { chatId } = await createOrGetSupportChat({
        userId: matchedUser.id,
        agentId: user.uid,
        openingMessage,
        agentLabel: "Support Center",
      });
      setSelectedChatId(chatId);
      await logAdminAction("INIT_SUPPORT_CHAT", { chatId, userId: matchedUser.id, email }, "initiated a support chat");
      toast.success("Support chat ready");
      setTargetEmail("");
    } catch (err) {
      toast.error(err?.message || "Failed to start support chat");
    } finally {
      setIsCreating(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const text = composer.trim();
    if (!selectedChatId || !text || !user?.uid || isSending) return;

    setIsSending(true);
    try {
      await sendSupportMessage({ chatId: selectedChatId, senderId: user.uid, text });
      await logAdminAction("SEND_SUPPORT_MESSAGE", { chatId: selectedChatId }, "sent a support message");
      setComposer("");
    } catch (err) {
      toast.error(err?.message || "Failed to send message");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col p-4 sm:p-6 gap-4">
      <header className="flex items-center gap-2">
        <Shield className="w-5 h-5 text-green-600 dark:text-green-400" />
        <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">Support Center Chat</h1>
      </header>

      <form onSubmit={handleCreateChat} className="bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl p-3 sm:p-4 flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="flex-1 min-w-0 flex gap-2 flex-col sm:flex-row">
          <input
            type="email"
            value={targetEmail}
            onChange={(e) => setTargetEmail(e.target.value)}
            placeholder="User email to start support chat"
            className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm text-gray-900 dark:text-white"
          />
          <input
            type="text"
            value={openingMessage}
            onChange={(e) => setOpeningMessage(e.target.value)}
            placeholder="Opening message"
            className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm text-gray-900 dark:text-white"
          />
        </div>
        <button
          type="submit"
          disabled={isCreating || !targetEmail.trim()}
          className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-60"
        >
          <PlusCircle className="w-4 h-4" />
          {isCreating ? "Starting..." : "Start Chat"}
        </button>
      </form>

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4">
        <aside className="bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl overflow-y-auto">
          {(chats || []).length === 0 ? (
            <div className="h-full flex items-center justify-center p-6 text-sm text-gray-500 dark:text-gray-400">
              No support chats yet.
            </div>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-neutral-700">
              {chats.map((chat) => {
                const isSelected = String(chat.id) === String(selectedChatId);
                const userProfile = profilesById[String(chat.supportUserId || "")];
                const userName = displayUserLabel(userProfile);
                return (
                  <li key={chat.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedChatId(chat.id)}
                      className={`w-full text-left p-3 transition ${isSelected ? "bg-green-50 dark:bg-green-900/20" : "hover:bg-gray-50 dark:hover:bg-neutral-700/60"}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{userName}</p>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{formatListTime(chat.updatedAt)}</span>
                      </div>
                      <div className="mt-0.5 flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-[10px] font-semibold">
                          <Shield className="w-3 h-3" />
                          {supportLabel(chat)}
                        </span>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{chat.lastMessage || "No messages yet"}</p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        <section className="bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl min-h-0 flex flex-col overflow-hidden">
          {selectedChat ? (
            <>
              <div className="px-4 py-3 border-b border-gray-200 dark:border-neutral-700 flex items-center gap-2">
                <UserRound className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {displayUserLabel(selectedUserProfile)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{supportLabel(selectedChat)}</p>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 dark:bg-neutral-900/40">
                {messages.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No messages yet.</p>
                ) : (
                  messages.map((msg) => {
                    const mine = String(msg.senderId) === String(user?.uid);
                    return (
                      <div key={msg.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-green-600 text-white" : "bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 text-gray-800 dark:text-gray-100"}`}>
                          {msg.text || ""}
                          <div className={`mt-1 text-[10px] ${mine ? "text-white/70" : "text-gray-500 dark:text-gray-400"}`}>
                            {formatTime(msg.createdAt)}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={bottomRef} />
              </div>

              <form onSubmit={handleSend} className="p-3 border-t border-gray-200 dark:border-neutral-700 flex items-center gap-2">
                <input
                  type="text"
                  value={composer}
                  onChange={(e) => setComposer(e.target.value)}
                  placeholder="Reply as Support Center"
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm text-gray-900 dark:text-white"
                />
                <button
                  type="submit"
                  disabled={isSending || !composer.trim()}
                  className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-60"
                >
                  <Send className="w-4 h-4" />
                  Send
                </button>
              </form>
            </>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center px-6">
              <MessageSquare className="w-10 h-10 text-gray-400 mb-2" />
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">Select a support conversation</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Open a chat from the left list to view and reply in real time.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
